// The outbox.
//
// A message is never lost just because there was no signal. It goes into the
// queue, shows up in the conversation as pending, and is sent once the network
// returns.
//
// The uuid is assigned once, when the item is queued, and repeated on every
// attempt. That is what makes sending idempotent: without it, retrying after a
// dropped connection would create a second order, because the client cannot tell
// "it never arrived" from "it arrived but the response was lost".

import { config } from '$lib/config';
import { contactById } from '$lib/db/contacts';
import { db, type OutboxRow } from '$lib/db/db';
import { addPendingMedia, deletePendingMedia, pendingMediaById } from '$lib/db/media-store';
import { saveEvents } from '$lib/db/threads';

import { respondToCart, sendMessage, uploadMedia } from './distributor-api';
import { asClientError, ClientError } from './errors';
import { retryDelayMs } from './http';
import { sessionToken } from './session';

let currentRun: Promise<void> | null = null;

/** Keeps the contact row's outbox summary (badge count, list ordering) current. */
async function refreshOutboxSummary(accountId: string): Promise<void> {
  const items = await db.outbox.where('accountId').equals(accountId).toArray();
  const newest = items.reduce<number | null>(
    (max, item) => (max === null || item.createdAtMs > max ? item.createdAtMs : max),
    null,
  );
  await db.contacts.update(accountId, {
    pendingCount: items.length,
    lastOutboxTimeMs: newest,
  });
}

/** Queues a message and immediately tries to send it. Media travels as bytes: the
 *  blob goes into pendingMedia and the queue row stays light. */
export async function queueMessage(input: {
  accountId: string;
  elements: Record<string, unknown>[];
  media?: { bytes: Uint8Array; contentType: string };
}): Promise<string> {
  const uuid = crypto.randomUUID();
  const pendingMediaId =
    input.media === undefined
      ? null
      : await addPendingMedia(input.media.bytes, input.media.contentType);
  await db.outbox.put({
    uuid,
    accountId: input.accountId,
    kind: 'message',
    payloadJson: JSON.stringify({ elements: input.elements }),
    pendingMediaId,
    mediaType: input.media?.contentType ?? null,
    createdAtMs: Date.now(),
    attempts: 0,
    nextAttemptMs: 0,
    lastErrorCode: null,
    permanentlyFailed: false,
  });
  await refreshOutboxSummary(input.accountId);
  void processOutbox().catch(() => undefined);
  return uuid;
}

export async function queueCartResponse(input: {
  accountId: string;
  cartId: string;
  decision: string;
  items?: { itemId: string; quantity: number }[];
  note?: string;
}): Promise<string> {
  const uuid = crypto.randomUUID();
  await db.outbox.put({
    uuid,
    accountId: input.accountId,
    kind: 'cart_response',
    payloadJson: JSON.stringify({
      cart_id: input.cartId,
      decision: input.decision,
      items: (input.items ?? []).map((p) => ({ item_id: p.itemId, quantity: p.quantity })),
      ...(input.note !== undefined ? { note: input.note } : {}),
    }),
    pendingMediaId: null,
    mediaType: null,
    createdAtMs: Date.now(),
    attempts: 0,
    nextAttemptMs: 0,
    lastErrorCode: null,
    permanentlyFailed: false,
  });
  await refreshOutboxSummary(input.accountId);
  void processOutbox().catch(() => undefined);
  return uuid;
}

/** Sends everything that is due, oldest first. A call while a run is active
 *  returns that run's promise — "drain the outbox" always means something
 *  finished, never a silent no-op. */
export function processOutbox(): Promise<void> {
  if (currentRun === null) {
    currentRun = drain().finally(() => {
      currentRun = null;
    });
  }
  return currentRun;
}

async function drain(): Promise<void> {
  const now = Date.now();
  const due = (await db.outbox.orderBy('nextAttemptMs').toArray())
    .filter((item) => !item.permanentlyFailed && item.nextAttemptMs <= now)
    .sort((a, b) => a.createdAtMs - b.createdAtMs);
  for (const item of due) {
    await sendItem(item);
  }
}

async function sendItem(item: OutboxRow): Promise<void> {
  const contact = await contactById(item.accountId);
  if (contact === undefined) {
    await removeItem(item);
    return;
  }

  // A withdrawn entry turns the thread read-only, and an entry change in progress
  // pauses sending. In both cases the item stays queued — the situation may still
  // change.
  if (contact.status !== 'active' || contact.sendingPaused) {
    return;
  }

  try {
    const token = await sessionToken(contact);
    if (item.kind === 'message') {
      await sendQueuedMessage(item, contact.serverUrl, contact.threadId ?? '', token);
    } else {
      await sendQueuedCartResponse(item, contact.serverUrl, token);
    }
    await removeItem(item);
  } catch (error) {
    await handleSendError(item, asClientError(error));
  }
}

async function sendQueuedMessage(
  item: OutboxRow,
  serverUrl: string,
  threadId: string,
  token: string,
): Promise<void> {
  const payload = JSON.parse(item.payloadJson) as Record<string, unknown>;
  let elements = Array.isArray(payload['elements'])
    ? (payload['elements'] as Record<string, unknown>[])
    : [];

  // The file first, then the element that refers to it. Splitting the steps allows
  // a large photo to be resumed without repeating the whole message.
  if (item.pendingMediaId !== null) {
    const media = await pendingMediaById(item.pendingMediaId);
    if (media === undefined) {
      throw new ClientError('not_found', { message: 'queued media is gone' });
    }
    const contentType = item.mediaType ?? media.contentType;
    let mediaId: string;
    try {
      mediaId = await uploadMedia({
        serverUrl,
        accountId: item.accountId,
        token,
        bytes: media.bytes,
        contentType,
      });
    } catch (error) {
      // The error catalogue requires the client to be able to shrink and retry.
      // Images can; a recording cannot, and fails honestly.
      if (
        error instanceof ClientError &&
        error.code === 'media_too_large' &&
        contentType.startsWith('image/')
      ) {
        const { shrinkImage } = await import('$lib/media/image');
        const smaller = await shrinkImage(media.bytes, contentType);
        await db.pendingMedia.update(item.pendingMediaId, {
          bytes: smaller.bytes.buffer as ArrayBuffer,
          contentType: smaller.contentType,
        });
        mediaId = await uploadMedia({
          serverUrl,
          accountId: item.accountId,
          token,
          bytes: smaller.bytes,
          contentType: smaller.contentType,
        });
      } else {
        throw error;
      }
    }
    elements = elements.map((element) =>
      element['type'] === 'image' || element['type'] === 'voice'
        ? { ...element, media_id: mediaId }
        : element,
    );
  }

  const event = await sendMessage({
    serverUrl,
    accountId: item.accountId,
    token,
    uuid: item.uuid,
    threadId,
    elements,
  });
  await saveEvents(item.accountId, [event]);
}

async function sendQueuedCartResponse(item: OutboxRow, serverUrl: string, token: string): Promise<void> {
  const payload = JSON.parse(item.payloadJson) as Record<string, unknown>;
  const items = Array.isArray(payload['items'])
    ? (payload['items'] as Record<string, unknown>[]).map((p) => ({
        itemId: typeof p['item_id'] === 'string' ? p['item_id'] : '',
        quantity: typeof p['quantity'] === 'number' ? p['quantity'] : 0,
      }))
    : [];
  const event = await respondToCart({
    serverUrl,
    accountId: item.accountId,
    token,
    uuid: item.uuid,
    cartId: typeof payload['cart_id'] === 'string' ? payload['cart_id'] : '',
    decision: typeof payload['decision'] === 'string' ? payload['decision'] : 'accept',
    items,
    ...(typeof payload['note'] === 'string' ? { note: payload['note'] } : {}),
  });
  await saveEvents(item.accountId, [event]);
}

async function handleSendError(item: OutboxRow, error: ClientError): Promise<void> {
  const attempt = item.attempts + 1;
  // Rejected content or a blocked link will not fix itself by repetition — keeping
  // such an item queued forever would only confuse the user.
  const permanent = !error.retryable || attempt > config.maxSendAttempts;
  await db.outbox.update(item.uuid, {
    attempts: attempt,
    lastErrorCode: error.code,
    permanentlyFailed: permanent,
    nextAttemptMs: permanent
      ? Number.MAX_SAFE_INTEGER
      : Date.now() + (error.retryAfterS !== undefined ? error.retryAfterS * 1000 : retryDelayMs(attempt)),
  });
  await refreshOutboxSummary(item.accountId);
}

async function removeItem(item: OutboxRow): Promise<void> {
  if (item.pendingMediaId !== null) {
    await deletePendingMedia(item.pendingMediaId);
  }
  await db.outbox.delete(item.uuid);
  await refreshOutboxSummary(item.accountId);
}

/** Connectivity returned: backoff deadlines computed under a dead network are
 *  void — everything still queued becomes due now and one drain runs. Permanent
 *  failures stay where they are; only the user revives those. */
export async function outboxOnline(): Promise<void> {
  await db.outbox
    .toCollection()
    .modify((item) => {
      if (!item.permanentlyFailed) {
        item.nextAttemptMs = 0;
        item.attempts = 0;
      }
    });
  await processOutbox();
}

/** Retries a permanently failed item — on the user's explicit request. */
export async function retryOutboxItem(uuid: string): Promise<void> {
  const item = await db.outbox.get(uuid);
  if (item === undefined) {
    return;
  }
  await db.outbox.update(uuid, { permanentlyFailed: false, attempts: 0, nextAttemptMs: 0 });
  await refreshOutboxSummary(item.accountId);
  await processOutbox();
}

export async function discardOutboxItem(uuid: string): Promise<void> {
  const item = await db.outbox.get(uuid);
  if (item === undefined) {
    return;
  }
  await removeItem(item);
}
